import { normalizeIngredientName } from '../ingredients/normalize-name.js';
import { parseIngredientLine } from '../ingredients/parse-line.js';
const BUILT_IN_COMPOUNDS = [
    'salz und pfeffer',
    'essig und öl',
    'öl und essig',
    'obst und gemüse',
    'wurst und käse',
    'milch und zucker',
    'salt and pepper',
    'oil and vinegar',
    'mac and cheese',
    'fish and chips',
    'half and half',
];
// Separator words need whitespace on both sides: inside a protected compound the spaces are
// replaced by a joiner, so "Salz und Pfeffer" is not split.
const SEPARATORS = /\s*(?:,|;|\.(?=\s|$))\s*|\s+(?:und dann|and then|sowie|außerdem|dazu noch|und|and|plus|dann|then|also)\s+/i;
const LEADING_FILLERS = [
    /^(?:ähm?|äh|öhm?|hm+|also|okay|ok|so|bitte|please|und|and|dann|then|außerdem|sowie|dazu|plus)(?=\s|,|$)[\s,]*/i,
    /^(?:wir|ich)\s+(?:brauchen|brauche|bräuchten|bräuchte|benötigen|benötige|haben keine?n?|habe keine?n?)\s+(?:noch\s+|auch\s+|außerdem\s+|dringend\s+)*/i,
    /^(?:es fehlt|es fehlen|uns fehlt|uns fehlen|mir fehlt|mir fehlen)\s+(?:noch\s+)?/i,
    /^(?:schreib|schreibe|setz|setze|pack|packe|füge|nimm)\s+(?:mir\s+|uns\s+)?(?:bitte\s+)?(?:noch\s+)?/i,
    /^(?:we|i)\s+(?:need|want|are out of|am out of|have no)\s+(?:some\s+|more\s+|also\s+)*/i,
    /^(?:add|put|get|buy)\s+(?:me\s+|us\s+)?(?:some\s+)?/i,
    /^(?:noch|auch|some|more)\s+/i,
];
const TRAILING_FILLERS = [
    /\s+(?:auf die (?:einkaufs)?liste|auf den (?:einkaufs)?zettel|auf|hinzu|dazu|bitte|noch)$/i,
    /\s+(?:to the (?:shopping )?list|on the (?:shopping )?list|please|too|as well)$/i,
];
// Number words at the start of a part. Longer phrases first.
const NUMBER_WORDS = {
    de: [
        [/^(?:ein(?:e|en|em)?\s+)?halbe[sr]?\s+/i, '0.5 '],
        [/^(?:anderthalb|eineinhalb)\s+/i, '1.5 '],
        [/^zweieinhalb\s+/i, '2.5 '],
        [/^(?:ein\s+)?dutzend\s+/i, '12 '],
        [/^(?:ein|eine|einen|einem|einer|eins)\s+/i, '1 '],
        [/^zwei\s+/i, '2 '],
        [/^drei\s+/i, '3 '],
        [/^vier\s+/i, '4 '],
        [/^fünf\s+/i, '5 '],
        [/^sechs\s+/i, '6 '],
        [/^sieben\s+/i, '7 '],
        [/^acht\s+/i, '8 '],
        [/^neun\s+/i, '9 '],
        [/^zehn\s+/i, '10 '],
        [/^elf\s+/i, '11 '],
        [/^zwölf\s+/i, '12 '],
        [/^zwanzig\s+/i, '20 '],
    ],
    en: [
        [/^half an?\s+/i, '0.5 '],
        [/^one and a half\s+/i, '1.5 '],
        [/^a dozen\s+/i, '12 '],
        [/^a couple of\s+/i, '2 '],
        [/^(?:a|an|one)\s+/i, '1 '],
        [/^two\s+/i, '2 '],
        [/^three\s+/i, '3 '],
        [/^four\s+/i, '4 '],
        [/^five\s+/i, '5 '],
        [/^six\s+/i, '6 '],
        [/^seven\s+/i, '7 '],
        [/^eight\s+/i, '8 '],
        [/^nine\s+/i, '9 '],
        [/^ten\s+/i, '10 '],
        [/^eleven\s+/i, '11 '],
        [/^twelve\s+/i, '12 '],
        [/^twenty\s+/i, '20 '],
    ],
};
// What a clean item name does not contain; its presence suggests a sentence, not an item.
const LEFTOVER = /\b(?:für|brauch\w*|kauf\w*|hol\w*|irgendwas|irgendetwas|etwas für|was für|need|buy|something|stuff)\b/i;
const JOINER = String.fromCharCode(0xe000); // private-use character: never part of a transcript
const escapeRegExp = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
function protectCompounds(text, knownNames) {
    const phrases = [...BUILT_IN_COMPOUNDS, ...knownNames.map((n) => n.toLowerCase().trim())]
        .filter((p) => /\s(?:und|and|plus|sowie)\s/.test(p))
        .sort((a, b) => b.length - a.length);
    let out = text;
    for (const phrase of phrases) {
        const pattern = new RegExp(`(^|[^\\p{L}])(${escapeRegExp(phrase)})(?=$|[^\\p{L}])`, 'giu');
        out = out.replace(pattern, (_m, before, hit) => before + hit.replace(/\s+/g, JOINER));
    }
    return out;
}
function stripFillers(part) {
    let current = part.trim();
    for (let pass = 0; pass < 4; pass++) {
        const before = current;
        for (const filler of LEADING_FILLERS)
            current = current.replace(filler, '');
        for (const filler of TRAILING_FILLERS)
            current = current.replace(filler, '');
        current = current.trim();
        if (current === before)
            break;
    }
    return current;
}
function numberWords(part, locale) {
    for (const [pattern, digits] of NUMBER_WORDS[locale]) {
        if (pattern.test(part))
            return part.replace(pattern, digits);
    }
    return part;
}
export function parseShoppingUtterance(text, locale = 'de', knownNames = []) {
    const cleaned = text.replace(/\s+/g, ' ').replace(/[!?]+/g, '.').trim();
    if (!cleaned)
        return { items: [], unreliable: true };
    const parts = protectCompounds(cleaned, knownNames).split(SEPARATORS);
    const items = [];
    const seen = new Set();
    let unreliable = false;
    for (const raw of parts) {
        const source = (raw ?? '').split(JOINER).join(' ').trim();
        const part = numberWords(stripFillers(source), locale);
        if (!part || /^\d+([.,]\d+)?$/.test(part))
            continue;
        const parsed = parseIngredientLine(part);
        let name = parsed.name.replace(/[.\s]+$/, '').trim();
        if (!name)
            continue;
        // Dictation engines write German nouns in capitals; typed or lower-cased input does not.
        if (locale === 'de' && name === name.toLowerCase())
            name = name[0].toUpperCase() + name.slice(1);
        const key = normalizeIngredientName(name);
        if (!key || seen.has(key))
            continue;
        seen.add(key);
        if (name.split(/\s+/).length > 4 || LEFTOVER.test(name))
            unreliable = true;
        items.push({
            name,
            quantity: parsed.quantity,
            quantityMax: parsed.quantityMax,
            quantityText: parsed.quantityText,
            unitCode: parsed.unitCode,
            unitText: parsed.unitText,
            note: parsed.note,
            source,
        });
    }
    return { items, unreliable: unreliable || items.length === 0 };
}
//# sourceMappingURL=utterance.js.map